import { Empty, Spin } from 'antd';
import { eventText, onTaskStatusEvents, taskStatusApi, type TaskStatusEvent } from 'common/realtime/taskStatusFeed';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './notification.scss';

type Props = { onUnreadChange: (n: number) => void; onNavigate?: () => void };

const Notificaton: React.FC<Props> = ({ onUnreadChange, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<TaskStatusEvent[]>([]);
  const [lastReadSeq, setLastReadSeq] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const busy = useRef(false);

  const load = useCallback(async (beforeSeq?: number) => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    try {
      const page = await taskStatusApi.recent(beforeSeq, 30);
      setItems((prev) => {
        const all = beforeSeq ? [...prev, ...page.items] : page.items;
        const seen = new Set<number>();
        return all.filter((e) => !seen.has(e.seq) && !!seen.add(e.seq));
      });
      setHasMore(page.hasMore);
      setLastReadSeq(page.lastReadSeq);
      onUnreadChange(page.unreadCount);
    } catch { /* keep what we have */ }
    finally { busy.current = false; setLoading(false); }
  }, [onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  // Live events while the panel is open: newest on top, no duplicates.
  useEffect(() => onTaskStatusEvents((events) => {
    setItems((prev) => {
      const seen = new Set(prev.map((e) => e.seq));
      const fresh = events.filter((e) => !seen.has(e.seq)).sort((a, b) => b.seq - a.seq);
      return [...fresh, ...prev];
    });
  }), []);

  const markAllRead = async () => {
    const top = items.reduce((m, e) => Math.max(m, e.seq), 0);
    if (top === 0) return;
    try {
      const r = await taskStatusApi.markRead(top);
      setLastReadSeq(r.lastReadSeq);
      onUnreadChange(r.unreadCount);
    } catch { /* ignore */ }
  };

  const open = (ev: TaskStatusEvent) => {
    onNavigate?.();
    navigate(`/manage-project/task-details/${ev.taskId}`);
  };

  return (
    <div className="panel" onClick={(e) => e.stopPropagation()}>
      <div className="header">
        <div className="title">{t('layout:taskStatus.title')}</div>
        <div className="read_all" role="button" onClick={markAllRead}>{t('layout:taskStatus.markAllRead')}</div>
      </div>
      <div
        ref={bodyRef}
        className="body custom-scrollbar"
        onScroll={(e) => {
          e.stopPropagation();
          const el = bodyRef.current;
          if (!el || !hasMore || loading) return;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
            load(items.reduce((m, x) => Math.min(m, x.seq), Number.MAX_SAFE_INTEGER));
          }
        }}
      >
        {loading && (
          <div className="flex justify-center items-center h-full w-full absolute z-50">
            <Spin className="loading" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('layout:taskStatus.empty')} />
        )}
        {items.map((ev) => (
          <div className="single_row cursor-pointer" key={ev.seq} onClick={() => open(ev)}>
            <div className="row_title">
              {ev.seq > lastReadSeq && <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2" />}
              {`${ev.displayId ?? ''} ${ev.taskName}`.trim()}
            </div>
            <div className="row_body">
              {eventText(ev, t)}
              {ev.projectName ? ` · ${ev.projectName}` : ''}
              {ev.isShared && ev.ownerOrgName ? ` · ${ev.ownerOrgName}` : ''}
            </div>
            <div className="row_footer">{moment(ev.createdAt).fromNow()}</div>
          </div>
        ))}
      </div>
      <div className="footer"></div>
    </div>
  );
};
export default Notificaton;
